/**
 * Error Handling Utilities
 *
 * Simple error handling for basic error management.
 * Will be expanded as app requirements become clearer.
 */

import { isDevelopment } from './featureFlags';

// Basic error categories
export type ErrorCategory =
  | 'NETWORK'
  | 'AUTH'
  | 'VALIDATION'
  | 'CLIENT'
  | 'SERVER'
  | 'UNKNOWN'
  | 'NOT_FOUND'
  | 'WEBSOCKET';

// Simple error class
export class AppError extends Error {
  public readonly category: ErrorCategory;
  public code?: string;
  public statusCode?: number;
  public details?: unknown;
  public requestId?: string;

  constructor(message: string, category: ErrorCategory = 'UNKNOWN') {
    super(message);
    this.name = 'AppError';
    this.category = category;

    // Maintains proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

// Basic error parsing
export const parseApiError = (error: unknown): AppError => {
  // Handle AppError instances
  if (error instanceof AppError) {
    return error;
  }

  // Handle fetch/network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new AppError('Network connection failed', 'NETWORK');
  }

  // Handle HTTP Response errors
  if (error instanceof Response) {
    const { status } = error;
    if (status === 401 || status === 403) {
      return new AppError('Authentication required', 'AUTH');
    }
    if (status === 422) {
      return new AppError('Validation failed', 'VALIDATION');
    }
    if (status >= 500) {
      return new AppError('Server error occurred', 'SERVER');
    }
    if (status >= 400) {
      return new AppError(`Request failed (${status})`, 'CLIENT');
    }
    return new AppError(`Request failed (${status})`, 'UNKNOWN');
  }

  // Handle generic Error instances
  if (error instanceof Error) {
    return new AppError(error.message);
  }

  // Handle unknown error types
  return new AppError('An unexpected error occurred');
};

// User-friendly error messages
export const getDisplayMessage = (error: AppError): string => {
  switch (error.category) {
    case 'NETWORK':
      return 'Connection error. Please check your internet connection and try again.';
    case 'AUTH':
      return 'Please sign in to continue.';
    case 'VALIDATION':
      return 'Please check the form for errors and try again.';
    case 'CLIENT':
      return 'Request could not be completed. Please refresh and try again.';
    case 'SERVER':
      return 'Server error. Please try again in a moment.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

// Simple error reporting
export const reportError = (error: AppError, context?: Record<string, unknown>): void => {
  // In development, log to console
  if (isDevelopment()) {
    console.group(`🚨 ${error.category} Error`);
    console.error(error.message);
    if (context) {
      console.log('Context:', context);
    }
    console.groupEnd();
  }

  // TODO: Add production error reporting when needed
};

// Basic helpers for common error types
export const createNetworkError = (message: string): AppError => {
  return new AppError(message, 'NETWORK');
};

export const createAuthError = (message: string): AppError => {
  return new AppError(message, 'AUTH');
};

export const createValidationError = (message: string): AppError => {
  return new AppError(message, 'VALIDATION');
};

export const createServerError = (message: string): AppError => {
  return new AppError(message, 'SERVER');
};

export const createNotFoundError = (message: string): AppError => {
  return new AppError(message, 'NOT_FOUND');
};

export const createWebSocketError = (message: string): AppError => {
  return new AppError(message, 'WEBSOCKET');
};

// Query error handler placeholder
export const handleQueryError = (error: unknown, queryKey?: string[]): AppError => {
  const appError = parseApiError(error);
  reportError(appError, queryKey ? { queryKey } : undefined);
  return appError;
};

export const handleMutationError = (error: unknown, mutationKey?: string[]): AppError => {
  const appError = parseApiError(error);
  reportError(appError, mutationKey ? { mutationKey } : undefined);
  return appError;
};

// Error utility functions
export const isRetryableError = (error: AppError): boolean => {
  return ['NETWORK', 'SERVER', 'WEBSOCKET'].includes(error.category);
};

export const shouldReportError = (error: AppError): boolean => {
  // Don't report validation errors or auth errors in development
  if (error.category === 'VALIDATION' || (error.category === 'AUTH' && isDevelopment())) {
    return false;
  }
  return true;
};

export const createErrorBoundaryFallback = (error: Error): string => {
  return `Error: ${error.message}`;
};

// Categorize error codes
export const categorizeErrorCode = (code: string): ErrorCategory => {
  if (code.includes('NETWORK') || code.includes('CONNECTION')) {
    return 'NETWORK';
  }
  if (code.includes('AUTH') || code.includes('UNAUTHORIZED')) {
    return 'AUTH';
  }
  if (code.includes('VALIDATION') || code.includes('INVALID')) {
    return 'VALIDATION';
  }
  if (code.includes('SERVER') || code.includes('INTERNAL')) {
    return 'SERVER';
  }
  if (code.includes('NOT_FOUND') || code.includes('404')) {
    return 'NOT_FOUND';
  }
  if (code.includes('WEBSOCKET') || code.includes('WS')) {
    return 'WEBSOCKET';
  }
  return 'UNKNOWN';
};
